declare global {
  interface Window {
    zE: any;
  }
}

export interface Agent {
  isAgent?: boolean;
  isEmployee?: boolean;
  email: string;
  firstName: string;
  lastName: string;
  customerId?: number;
  customerAgents?: [];
}

export interface SupportFormOffset {
  horizontal?: string;
  vertical?: string;
}

const SNIPPET_ID = 'ze-snippet';
const SNIPPET_SRC = 'https://static.zdassets.com/ekr/snippet.js?key=f4db8e11-0d6c-405f-b77c-8a86bc0f1e67';

/** Keyed on the snippet tag, not window.zE, which only appears once loading finishes. */
export const isZendeskLoaded = (): boolean => Boolean(document.querySelector(`#${SNIPPET_ID}`));

/**
 * Injects the Zendesk widget and suppresses its floating launcher. Idempotent.
 *
 * On demand rather than a static tag, because the public pages only learn which widget to
 * load after fetching /v3/intercom_config/.
 */
export const loadZendesk = () => {
  if (isZendeskLoaded()) return;

  const script = document.createElement('script');
  script.id = SNIPPET_ID;
  script.async = true;
  script.src = SNIPPET_SRC;
  // Suppress Zendesk's own launcher; support is opened via openZendeskSupportForm.
  script.addEventListener('load', () => {
    if (window.zE) {
      window.zE(() => { window.zE.hide(); });
    }
  });

  document.head.appendChild(script);
};

/**
 * this activates zendesk form and make it visible, also prefills name and email of the logged in session if agent data is provided
 * @param sidebarExpanded boolean param for whether sidebar is expanded or not
 * @param agent this is an object of the agent data, must include firstName, lastName, and email.
 */

export const openZendeskSupportForm = (
  sidebarExpanded: boolean,
  agent: Agent,
  offsetOverride?: SupportFormOffset,
  positionOverride?: SupportFormOffset,
) => {
  const { firstName, lastName, email } = agent;
  if (window.zE) {
    window.zE(() => {
      if (agent?.email) {
        window.zE('webWidget', 'prefill', {
          name: {
            value: `${firstName} ${lastName}`,
            readOnly: true,
          },
          email: {
            value: email,
            readOnly: true,
          },
        });
      } else {
        window.zE('webWidget', 'prefill', {
          name: {
            readOnly: false,
          },
          email: {
            readOnly: false,
          },
        });
      }

      window.zE('webWidget', 'updateSettings',
        {
          offset: offsetOverride || {
            horizontal: sidebarExpanded ? '233px' : '85px',
          },
          position: positionOverride || { horizontal: 'left', vertical: 'bottom' },
          answerBot: {
            suppress: false,
            contactOnlyAfterQuery: true,
          },
          contactOptions: {
            contactFormLabel: { '*': 'Contact Support' },
            contactButton: { '*': 'Contact Support' },
          },
        },
      );

      window.zE.activate();
    });
  }
};

export default {
  isZendeskLoaded,
  loadZendesk,
  openZendeskSupportForm,
};
